import {
    Alert,
    alpha,
    Box,
    Button,
    ButtonBase,
    Skeleton,
    Stack,
    Typography as MuiTypography,
    useTheme,
} from "@mui/material";
import { PackagingsGrid } from "./PackagingsGrid";
import {
    PackagingListContextProvider,
    type PackagingListTab,
    type PackagingListTabId,
    usePackagingListContext,
} from "./PackagingListContext";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef } from "react";
import { EXTENSION_POINTS, ExtensionPoint } from "src/components/PuckExtensions";
import { CountTabs as FoodsCountTabs, Typography as FoodsTypography } from "@foodpilot/foods";
import { PackagingCreate } from "./PackagingCreate";
import { visuallyHidden } from "@mui/utils";
import { EntityListLayout } from "src/components/Grid";
import { GridPageHeader } from "src/components/Grid/PageGridHeader";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";

const PackagingListComponent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const isProductUxV2 = useIsProductUxV2();
    const {
        activeTab,
        globalSearch,
        hasData,
        hasError,
        hasTabsError,
        isTabsLoading,
        retryActiveQuery,
        retryTabsQueries,
        setActiveTab,
        tabs,
    } = usePackagingListContext();
    const { company } = useCompanyMetadata();
    const { search, setSearch } = globalSearch;
    const pageTitle = isProductUxV2 ? t("packagings.list.title", "My packagings") : t("packagings");
    const gridPanelId = "packagings-grid-panel";
    const activeTabElementId = `packagings-tab-${activeTab}`;

    const accessibleTabs = useMemo(
        () =>
            tabs.map((tab) => ({
                ...tab,
                tabProps: {
                    id: `packagings-tab-${tab.id}`,
                    "aria-controls": gridPanelId,
                },
            })),
        [tabs],
    );
    const toolbarHostsTabs = isProductUxV2 && !(hasError && !hasData);
    const gridPanelProps = { id: gridPanelId, role: "tabpanel", "aria-labelledby": activeTabElementId };
    const restoreTabFocus = useRef(false);
    const lastFocusedTabElementId = useRef<string | null>(null);
    const selectTab = (tab: PackagingListTabId) => {
        restoreTabFocus.current = tab !== activeTab;
        setActiveTab(tab);
    };

    useEffect(() => {
        const lostFocusedTab =
            lastFocusedTabElementId.current === activeTabElementId && document.activeElement === document.body;

        if (!restoreTabFocus.current && !lostFocusedTab) return;

        restoreTabFocus.current = false;
        lastFocusedTabElementId.current = activeTabElementId;
        document.getElementById(activeTabElementId)?.focus();
    }, [activeTabElementId, toolbarHostsTabs]);

    const typeTabs =
        isProductUxV2 ?
            <FoodsCountTabs
                activeId={activeTab}
                ariaLabel={t("packagings")}
                isLoading={isTabsLoading}
                onChange={selectTab}
                tabs={accessibleTabs}
            />
        :   <LegacyCountTabs activeId={activeTab} isLoading={isTabsLoading} onChange={setActiveTab} tabs={tabs} />;

    return (
        <EntityListLayout
            appearance={isProductUxV2 ? "hub" : "legacy"}
            ariaLabel={pageTitle}
            heading={
                isProductUxV2 ?
                    <GridPageHeader title={pageTitle} />
                :   <PackagingPageTitle title={pageTitle} companyName={company.name} />
            }
            hideHeadingOnMobile
            mobileHeading={
                isProductUxV2 ?
                    <FoodsTypography variant="h2" sx={visuallyHidden}>
                        {pageTitle}
                    </FoodsTypography>
                :   <MuiTypography variant="h2" sx={visuallyHidden}>
                        {pageTitle}
                    </MuiTypography>
            }
            search={{
                onChange: setSearch,
                placeholder: t("rechercher_un_element"),
                showOnDesktop: false,
                value: search,
            }}
            actions={<PackagingCreate />}
            mobileActions={isProductUxV2 ? <PackagingCreate /> : undefined}
            beforeGrid={<ExtensionPoint extensionPoint={EXTENSION_POINTS.PACKAGING_LISTING_HEADER} priority="high" />}
            gridHeader={
                (!toolbarHostsTabs || hasTabsError) && (
                    <Box
                        sx={{
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            padding: theme.spacing(2),
                        }}
                    >
                        {!toolbarHostsTabs && typeTabs}
                        {hasTabsError && (
                            <PackagingListError
                                appearance={isProductUxV2 ? "product-v2" : "legacy"}
                                message={t("une_erreur_est_survenue", "An error occurred")}
                                onRetry={retryTabsQueries}
                                retryLabel={t("common.actions.refresh", "Refresh")}
                            />
                        )}
                    </Box>
                )
            }
            afterGrid={<ExtensionPoint extensionPoint={EXTENSION_POINTS.PACKAGING_LISTING_FOOTER} priority="high" />}
        >
            <Box {...(toolbarHostsTabs ? {} : gridPanelProps)} sx={{ minWidth: 0 }}>
                {hasError && hasData && (
                    <PackagingListError
                        appearance={isProductUxV2 ? "product-v2" : "legacy"}
                        message={t("une_erreur_est_survenue", "An error occurred")}
                        onRetry={retryActiveQuery}
                        retryLabel={t("common.actions.refresh", "Refresh")}
                    />
                )}
                {hasError && !hasData ?
                    <Stack minHeight="160px" alignItems="center" justifyContent="center" padding={2}>
                        <PackagingListError
                            appearance={isProductUxV2 ? "product-v2" : "legacy"}
                            message={t("une_erreur_est_survenue", "An error occurred")}
                            onRetry={retryActiveQuery}
                            retryLabel={t("common.actions.refresh", "Refresh")}
                        />
                    </Stack>
                :   <PackagingsGrid
                        viewTabsLeading={toolbarHostsTabs ? typeTabs : undefined}
                        tablePanelProps={toolbarHostsTabs ? gridPanelProps : undefined}
                    />
                }
            </Box>
        </EntityListLayout>
    );
};

type PackagingPageTitleProps = {
    title: string;
    companyName: string;
};

const PackagingPageTitle = ({ title, companyName }: PackagingPageTitleProps) => {
    const theme = useTheme();
    const { containerDivider } = theme.custom.strokes;

    return (
        <Stack direction="row" useFlexGap alignItems="center" gap={theme.spacing(2)}>
            <MuiTypography variant="h2">{title}</MuiTypography>
            <Box
                aria-hidden
                sx={{
                    alignSelf: "stretch",
                    borderLeft: `${containerDivider.width} ${containerDivider.style} ${containerDivider.color}`,
                }}
            />
            <MuiTypography variant="big-bold" color={theme.custom.grey[2800]}>
                {companyName}
            </MuiTypography>
        </Stack>
    );
};

type LegacyCountTabsProps = {
    tabs: PackagingListTab[];
    activeId: PackagingListTabId;
    onChange: (id: PackagingListTabId) => void;
    isLoading: boolean;
};

const LegacyCountTabs = ({ tabs, activeId, onChange, isLoading }: LegacyCountTabsProps) => {
    const theme = useTheme();
    const tokens = theme.custom.countTabs;
    const stroke = theme.custom.strokes.containerDivider;

    return (
        <Stack direction="row" flexWrap="wrap" gap={tokens.gap}>
            {tabs.map((tab) => {
                const isSelected = tab.id === activeId;

                return (
                    <ButtonBase
                        key={tab.id}
                        id={`packagings-tab-${tab.id}`}
                        aria-controls="packagings-grid-panel"
                        aria-label={`${tab.label} - ${tab.count}`}
                        aria-pressed={isSelected}
                        onClick={() => onChange(tab.id)}
                        sx={{
                            alignItems: "center",
                            background: isSelected ? tokens.selected.background : tokens.default.background,
                            border: `${stroke.width} ${stroke.style} ${
                                isSelected ? tokens.selected.borderColor : tokens.default.borderColor
                            }`,
                            borderRadius: tokens.borderRadius,
                            boxShadow: isSelected ? tokens.selected.boxShadow : tokens.default.boxShadow,
                            color: isSelected ? tokens.selected.color : tokens.default.color,
                            display: "inline-flex",
                            minHeight: tokens.minHeight,
                            padding: tokens.padding,
                        }}
                    >
                        <MuiTypography component="span" variant="body-bold" color="inherit">
                            {tab.label}
                        </MuiTypography>
                        <MuiTypography
                            component="span"
                            variant="body-bold"
                            color="inherit"
                            sx={{ px: tokens.separatorPaddingInline }}
                        >
                            {"-"}
                        </MuiTypography>
                        {isLoading ?
                            <Skeleton
                                variant="text"
                                width={tokens.loadingWidth}
                                sx={{
                                    bgcolor:
                                        isSelected ?
                                            alpha(tokens.selected.color, tokens.loadingSelectedOpacity)
                                        :   undefined,
                                }}
                            />
                        :   <MuiTypography component="span" variant="body-bold" color="inherit">
                                {tab.count}
                            </MuiTypography>
                        }
                    </ButtonBase>
                );
            })}
        </Stack>
    );
};

type PackagingListErrorProps = {
    appearance: "legacy" | "product-v2";
    message: string;
    onRetry: () => void;
    retryLabel: string;
};

const PackagingListError = ({ appearance, message, onRetry, retryLabel }: PackagingListErrorProps) => {
    if (appearance === "product-v2") {
        return <RemoteErrorAlert message={message} onRetry={onRetry} retryLabel={retryLabel} status="server-error" />;
    }

    return (
        <Alert
            severity="error"
            action={
                <Button color="inherit" onClick={onRetry}>
                    {retryLabel}
                </Button>
            }
        >
            {message}
        </Alert>
    );
};

const PackagingList = () => {
    const isProductUxV2 = useIsProductUxV2();

    return (
        <PackagingListContextProvider appearance={isProductUxV2 ? "product-v2" : "legacy"}>
            <PackagingListComponent />
        </PackagingListContextProvider>
    );
};
export default PackagingList;
